import { domainError, SHARED_ERROR, type DomainError } from '../shared/domain-error';
import { err, ok, type Result } from '../shared/result';
import { BusinessDate, eachDayOfRange } from './business-date';

const DAYS_IN_WEEK = 7;
const MIN_WEEK = 1;
const MAX_WEEK = 53;

/**
 * Semana ISO-8601 (RN-122).
 *
 * Começa na segunda-feira; a semana 1 é a que contém a primeira quinta-feira do ano.
 * `isoYear` pode diferir do ano civil na virada — 31/12/2026 pertence à semana 53 de
 * 2026, mas 01/01/2027 também. É por isso que os dois campos são armazenados.
 */
export class IsoWeek {
  private constructor(
    readonly isoYear: number,
    readonly isoWeek: number,
  ) {}

  static create(isoYear: number, isoWeek: number): Result<IsoWeek, DomainError> {
    if (!Number.isInteger(isoYear) || !Number.isInteger(isoWeek)) {
      return err(
        domainError(SHARED_ERROR.INVALID_WEEK, 'Ano e semana devem ser inteiros.', {
          isoYear,
          isoWeek,
        }),
      );
    }
    if (isoWeek < MIN_WEEK || isoWeek > MAX_WEEK) {
      return err(
        domainError(SHARED_ERROR.INVALID_WEEK, 'Semana ISO deve estar entre 1 e 53.', {
          isoYear,
          isoWeek,
        }),
      );
    }
    const candidate = new IsoWeek(isoYear, isoWeek);
    // A semana 53 não existe em todos os anos: valide voltando pela data de início.
    const roundTrip = IsoWeek.of(candidate.startDate());
    if (roundTrip.isoYear !== isoYear || roundTrip.isoWeek !== isoWeek) {
      return err(
        domainError(
          SHARED_ERROR.INVALID_WEEK,
          `A semana ${isoWeek} não existe no ano ${isoYear}.`,
          {
            isoYear,
            isoWeek,
          },
        ),
      );
    }
    return ok(candidate);
  }

  /** Para literais confiáveis. Lança se inválido. */
  static ofParts(isoYear: number, isoWeek: number): IsoWeek {
    const result = IsoWeek.create(isoYear, isoWeek);
    if (!result.ok) throw new Error(result.error.message);
    return result.value;
  }

  static of(date: BusinessDate): IsoWeek {
    // A quinta-feira da semana determina o ano ISO.
    const thursday = date.addDays(4 - date.weekday());
    const isoYear = thursday.year;
    const firstThursday = IsoWeek.firstThursdayOf(isoYear);
    const isoWeek = Math.round(firstThursday.daysUntil(thursday) / DAYS_IN_WEEK) + 1;
    return new IsoWeek(isoYear, isoWeek);
  }

  /** A primeira quinta-feira do ano define a semana 1. */
  private static firstThursdayOf(year: number): BusinessDate {
    const jan4 = BusinessDate.of(`${String(year).padStart(4, '0')}-01-04`);
    return jan4.addDays(4 - jan4.weekday());
  }

  /** Segunda-feira. */
  startDate(): BusinessDate {
    const firstThursday = IsoWeek.firstThursdayOf(this.isoYear);
    const firstMonday = firstThursday.subtractDays(3);
    return firstMonday.addDays((this.isoWeek - 1) * DAYS_IN_WEEK);
  }

  /** Domingo. */
  endDate(): BusinessDate {
    return this.startDate().addDays(DAYS_IN_WEEK - 1);
  }

  /** Os sete dias da semana, de segunda a domingo. */
  days(): BusinessDate[] {
    const range = eachDayOfRange(this.startDate(), this.endDate());
    /* c8 ignore next -- intervalo construído internamente, nunca invertido */
    if (!range.ok) throw new Error(range.error.message);
    return range.value;
  }

  contains(date: BusinessDate): boolean {
    return date.isBetween(this.startDate(), this.endDate());
  }

  next(): IsoWeek {
    return IsoWeek.of(this.startDate().addDays(DAYS_IN_WEEK));
  }

  previous(): IsoWeek {
    return IsoWeek.of(this.startDate().subtractDays(DAYS_IN_WEEK));
  }

  compare(other: IsoWeek): number {
    return this.isoYear - other.isoYear || this.isoWeek - other.isoWeek;
  }

  equals(other: IsoWeek): boolean {
    return this.isoYear === other.isoYear && this.isoWeek === other.isoWeek;
  }

  /** `"2026-W34"` — chave estável para índice e URL. */
  key(): string {
    return `${this.isoYear}-W${String(this.isoWeek).padStart(2, '0')}`;
  }

  /** `"Semana 34 · 17/08 a 23/08/2026"` */
  toDisplay(): string {
    const start = this.startDate();
    const end = this.endDate();
    return `Semana ${this.isoWeek} · ${String(start.day).padStart(2, '0')}/${String(
      start.month,
    ).padStart(2, '0')} a ${end.toDisplay()}`;
  }

  toString(): string {
    return this.key();
  }
}
